import React from 'react'
import { Logger } from 'wdc-cube'

import CloseIcon from '@mui/icons-material/Close'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'

import { formatCurrency, parseNonNegativeDecimal } from 'src/utils/formatter-utils'
import {
    bannerSx,
    buttonSx,
    classToFComponent,
    FCClassContext,
    Field,
    FieldLabel,
    FooterNote,
    inputSx,
    ModalClose,
    ModalContent,
    ModalFooter,
    ModalFooterActions,
    ModalHead
} from 'src/utils/views'

import { BillingInvoiceDialogScope } from '../bl_scopes'

const LOG = Logger.get('BL-INVOICE-DIALOG')

type InvoiceDialogProps = {
    scope: BillingInvoiceDialogScope
}

class InvoiceDialogViewClass implements FCClassContext<InvoiceDialogProps> {
    scope!: BillingInvoiceDialogScope

    readonly emitCancel = () => this.scope.onCancel().catch(LOG.caught)
    readonly emitConfirm = () => this.scope.onConfirm().catch(LOG.caught)
    readonly emitIsentarTotal = () => this.scope.onIsentarTotal().catch(LOG.caught)

    readonly emitDescontoChanged = (e: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onDescontoChanged(e.target.value).catch(LOG.caught)

    readonly emitObservacaoChanged = (e: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onObservacaoChanged(e.target.value).catch(LOG.caught)

    readonly emitMotivoChanged = (e: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onMotivoChanged(e.target.value).catch(LOG.caught)

    render({ scope }: InvoiceDialogProps) {
        this.scope = scope

        const reversing = scope.mode === 'REVERSE'

        return (
            <Dialog
                open
                onClose={this.emitCancel}
                maxWidth={false}
                slotProps={{
                    paper: {
                        sx: {
                            width: 'min(720px, calc(100vw - 28px))',
                            borderRadius: 'var(--radius)',
                            boxShadow: 'var(--shadow-2)',
                            backgroundImage: 'none'
                        }
                    }
                }}
            >
                <ModalContent>
                    <ModalHead>
                        <div>
                            <h2>{reversing ? 'Estornar faturamento' : 'Faturar competência'}</h2>
                            <p>
                                {scope.entityName} :: {scope.competenciaLabel}
                            </p>
                        </div>
                        <ModalClose aria-label="Fechar" onClick={this.emitCancel} disabled={scope.saving}>
                            <CloseIcon />
                        </ModalClose>
                    </ModalHead>

                    {reversing ? this.renderReverse() : this.renderInvoice()}

                    {!!scope.errorMessage && (
                        <Alert
                            variant="outlined"
                            icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
                            sx={bannerSx('danger')}
                        >
                            {scope.errorMessage}
                        </Alert>
                    )}
                </ModalContent>
                <ModalFooter>
                    <FooterNote>{this.renderFooterNote()}</FooterNote>
                    <ModalFooterActions>
                        <Button sx={buttonSx()} onClick={this.emitCancel} disabled={scope.saving}>
                            Cancelar
                        </Button>
                        <Button
                            sx={buttonSx(reversing ? 'danger' : 'primary')}
                            onClick={this.emitConfirm}
                            disabled={scope.saving}
                            startIcon={scope.saving ? <CircularProgress size={16} /> : undefined}
                        >
                            {reversing ? 'Estornar faturamento' : 'Faturar competência'}
                        </Button>
                    </ModalFooterActions>
                </ModalFooter>
            </Dialog>
        )
    }

    renderFooterNote() {
        const { scope } = this

        if (scope.mode === 'REVERSE') {
            return 'A competência volta para “A faturar”.'
        }

        const { aCobrar } = this.computeInvoice()

        return aCobrar > 0
            ? `Será cobrado ${formatCurrency(aCobrar)} nesta competência.`
            : 'A competência será fechada sem cobrança.'
    }

    computeInvoice() {
        const { scope } = this

        const desconto = parseNonNegativeDecimal(scope.descontoText)
        const excedeu = desconto !== undefined && desconto > scope.valorCalculado
        const aCobrar = desconto === undefined || excedeu ? scope.valorCalculado : scope.valorCalculado - desconto

        return { excedeu, aCobrar }
    }

    renderInvoice() {
        const { scope } = this
        const { excedeu, aCobrar } = this.computeInvoice()

        return (
            <>
                <Field>
                    <FieldLabel htmlFor="blValorApurado">Valor apurado</FieldLabel>
                    <TextField
                        id="blValorApurado"
                        size="small"
                        value={formatCurrency(scope.valorCalculado)}
                        sx={inputSx}
                        fullWidth
                        slotProps={{
                            input: { readOnly: true }
                        }}
                    />
                </Field>
                <Field>
                    <FieldLabel htmlFor="blDesconto">Desconto (cortesia)</FieldLabel>
                    <TextField
                        id="blDesconto"
                        size="small"
                        type="number"
                        value={scope.descontoText}
                        onChange={this.emitDescontoChanged}
                        error={excedeu}
                        helperText={
                            excedeu
                                ? 'O desconto não pode passar do valor apurado.'
                                : 'O que for abatido aqui deixa de ser cobrado e fica registrado como cortesia.'
                        }
                        sx={inputSx}
                        fullWidth
                        slotProps={{
                            input: {
                                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                inputProps: { min: 0, step: 0.01, max: scope.valorCalculado }
                            }
                        }}
                    />
                </Field>
                <div>
                    <Button
                        sx={buttonSx('tertiary', true)}
                        onClick={this.emitIsentarTotal}
                        disabled={scope.valorCalculado <= 0}
                    >
                        Isentar o valor todo
                    </Button>
                </div>
                <Alert
                    variant="outlined"
                    icon={
                        aCobrar > 0 ? (
                            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                        ) : (
                            <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                        )
                    }
                    sx={bannerSx(aCobrar > 0 ? 'blue' : 'green')}
                >
                    {aCobrar > 0 ? (
                        <>
                            Valor a cobrar: <strong>{formatCurrency(aCobrar)}</strong>
                        </>
                    ) : (
                        <>
                            Competência <strong>isenta</strong> — nada será cobrado.
                        </>
                    )}
                </Alert>
                <Field>
                    <FieldLabel htmlFor="blObservacao">Observação</FieldLabel>
                    <TextField
                        id="blObservacao"
                        size="small"
                        value={scope.observacao}
                        onChange={this.emitObservacaoChanged}
                        placeholder="Nº da nota, motivo da cortesia…"
                        helperText="Fica registrada no histórico do faturamento e aparece no tooltip da linha."
                        multiline
                        minRows={2}
                        sx={inputSx}
                        fullWidth
                    />
                </Field>
            </>
        )
    }

    renderReverse() {
        const { scope } = this

        return (
            <>
                <Alert variant="outlined" icon={<WarningAmberIcon sx={{ fontSize: 16 }} />} sx={bannerSx('warning')}>
                    O faturamento desta competência será desfeito e a linha volta para “A faturar”. O fechamento
                    anterior fica registrado no histórico.
                </Alert>
                <Field>
                    <FieldLabel htmlFor="blMotivo">Motivo do estorno</FieldLabel>
                    <TextField
                        id="blMotivo"
                        size="small"
                        value={scope.motivo}
                        onChange={this.emitMotivoChanged}
                        helperText="Explique o que motivou o estorno — é o que ficará no histórico da competência."
                        multiline
                        minRows={2}
                        sx={inputSx}
                        fullWidth
                    />
                </Field>
            </>
        )
    }
}

export const BillingInvoiceDialogView = classToFComponent(InvoiceDialogViewClass, React)
export default BillingInvoiceDialogView
